import { useEffect, useState } from 'react'

/**
 * Hook customizado que aplica debounce a um valor.
 * Ele só atualiza o valor retornado após um tempo especificado sem mudanças no valor de entrada.
 * @param value O valor a ser debounced (pode ser string, objeto, etc.).
 * @param delay O tempo de espera em milissegundos.
 * @returns O valor "estabilizado" após o debounce.
 */
export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value)

    useEffect(() => {
        // Cria um timer que só atualizará o valor debounced após o 'delay'
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        // A função de limpeza é crucial: ela cancela o timer anterior
        // se o 'value' mudar antes que o 'delay' tenha passado.
        return () => {
            clearTimeout(handler)
        }
    }, [value, delay]) // Só re-executa o efeito se o valor ou o delay mudarem

    return debouncedValue
}
